/*
 * How the to-do screens say when: one en-GB, 24-hour voice for every date and
 * time - rows, the Completed view, the bell, comments, reminders. Pure: no
 * React, no imports, so anything can use it. `now` is passed in by tests and
 * defaults to the clock.
 *
 *   dayLabel('2026-10-20')          'Today' | 'Yesterday' | 'Tomorrow' | 'Tue 20 Oct' | 'Tue 20 Oct 2025'
 *   timeLabel('15:00:00')           '15:00'
 *   relative('2026-10-20T06:00Z')   'Just now' | '5 min ago' | '3 h ago' | 'Yesterday 15:00' | 'Mon 15:00' | '12 Oct' | '12 Oct 2025'
 *   dueChip('2026-10-20', '15:00')  'Today 15:00' | 'Tomorrow' | 'Fri' | '25 Oct' | '25 Oct 2027'
 */

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');

/** A 'YYYY-MM-DD' (or anything starting with one), a Date or an ISO stamp, as a local midnight. */
const toDay = (v) => {
  if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const s = String(v || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10)) && (s.length === 10 || !/[T ]\d/.test(s.slice(10, 12)))) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const diffDays = (a, b) => Math.round((b - a) / 86400000);

/** 'HH:MM' from 'HH:MM', 'HH:MM:SS' or a Date - always 24-hour. */
export const timeLabel = (t) => {
  if (!t) return '';
  if (t instanceof Date) return `${pad(t.getHours())}:${pad(t.getMinutes())}`;
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  return m ? `${pad(m[1])}:${m[2]}` : String(t);
};

/** A day as a heading says it: Today, Yesterday, Tomorrow, else "Tue 20 Oct" (+ year when not this one). */
export const dayLabel = (date, now = new Date()) => {
  const d = toDay(date);
  if (!d) return '';
  const n = diffDays(toDay(now), d);
  if (n === 0) return 'Today';
  if (n === -1) return 'Yesterday';
  if (n === 1) return 'Tomorrow';
  const base = `${WD[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
};

/** How long ago a moment was, the way a feed says it. */
export const relative = (iso, now = new Date()) => {
  const at = iso instanceof Date ? iso : new Date(iso);
  if (!iso || Number.isNaN(at.getTime())) return '';
  const secs = (now - at) / 1000;
  if (secs < 60 && secs > -60) return 'Just now';
  if (secs > 0 && secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  const n = diffDays(toDay(now), toDay(at));
  if (secs > 0 && secs < 6 * 3600 && n === 0) return `${Math.floor(secs / 3600)} h ago`;
  const time = timeLabel(at);
  if (n === 0) return `Today ${time}`;
  if (n === -1) return `Yesterday ${time}`;
  if (n === 1) return `Tomorrow ${time}`;
  if (n < 0 && n > -7) return `${WD[at.getDay()]} ${time}`;
  const base = `${at.getDate()} ${MON[at.getMonth()]}`;
  return at.getFullYear() === now.getFullYear() ? base : `${base} ${at.getFullYear()}`;
};

/*
 * PLAIN DATES AND TIMES, the way a Maltese firm writes them: en-GB, the
 * firm's clock (Europe/Malta), 24-hour. Twenty-three files called the bare
 * toLocaleDateString() with no locale, so a machine set to en-US showed
 * 10/26/2026 and a machine in another timezone showed the wrong day. These
 * three are what those calls become. Anything unparseable is '' rather than
 * "Invalid Date".
 *
 *   fmtDate('2026-10-26T06:00Z')       '26 Oct 2026'
 *   fmtDateTime('2026-10-26T06:00Z')   '26 Oct 2026, 08:00'
 *   fmtTime('2026-10-26T06:00Z')       '08:00'
 */
const LOCALE = 'en-GB';
const ZONE = 'Europe/Malta';
const asDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmt = (v, options) => {
  const d = asDate(v);
  if (!d) return '';
  try {
    return d.toLocaleString(LOCALE, { timeZone: ZONE, ...options });
  } catch {
    // A runtime without that zone's data: the local clock is better than nothing.
    return d.toLocaleString(LOCALE, options);
  }
};
export const fmtDate = (v) => fmt(v, { day: 'numeric', month: 'short', year: 'numeric' });
export const fmtTime = (v) => fmt(v, { hour: '2-digit', minute: '2-digit', hour12: false });
export const fmtDateTime = (v) => {
  const d = fmtDate(v);
  return d ? `${d}, ${fmtTime(v)}` : '';
};

/** A due date on a chip, Todoist-style: Today, Tomorrow, Yesterday, a weekday this week, else "25 Oct". */
export const dueChip = (date, time, now = new Date()) => {
  const t = timeLabel(time);
  const d = date ? toDay(date) : null;
  if (!d) return t;
  const n = diffDays(toDay(now), d);
  let label;
  if (n === 0) label = 'Today';
  else if (n === 1) label = 'Tomorrow';
  else if (n === -1) label = 'Yesterday';
  else if (n > 1 && n < 7) label = WD[d.getDay()];
  else {
    label = `${d.getDate()} ${MON[d.getMonth()]}`;
    if (d.getFullYear() !== now.getFullYear()) label += ` ${d.getFullYear()}`;
  }
  return t ? `${label} ${t}` : label;
};
